<template>
  <b-modal
    id="checklist-template-modal"
    ref="checklist-template-modal"
    :title="templateEdit === null ? $t('message.settings.template.checklist.createChecklistTemplate') : $t('message.settings.template.editTemplate')"
    hide-footer
    no-close-on-backdrop
    @close="$emit('on-close')">
    <div class="row">
      <div class="col-sm-12 col-md-12 mb-2">
        <coozzy-form-input
          v-model="template.name"
          :limit="255"
          :name="$t('message.settings.template.name') + '*'"
          :state="$v.template.name.$dirty && $v.template.name.$error ? false : null"
          class="mb-2" />
      </div>
      <div class="col-sm-12 col-md-12 mb-2">
        <label>{{ $t('message.ticketOverview.ticket.assignee') + '*' }}</label>
        <v-select
          :key="listAllEmployeesChecklist.length"
          v-model="assignee"
          :class="$v.assignee.$dirty && $v.assignee.$error ? 'is-invalid' : ''"
          :filter="searchAssignee"
          :options="listAllEmployeesChecklist"
          class="assignee">
          <template #option="option">
            <img
              :alt="option.label"
              :src="option.value?.profile?.pictureUrl || '/avatar.png'"
              class="align-self-center avatar"
              height="15px">
            {{ option.label }} <b>{{ option.company ? '-' + option.company : '' }}</b>
          </template>
          <template #selected-option="option">
            <div>
              <img
                :alt="option.label"
                :src="option.value?.profile?.pictureUrl || '/avatar.png'"
                class="align-self-center avatar"
                height="15">
              {{ option.label }} <b>{{ option.company ? '-' + option.company : '' }}</b>
            </div>
          </template>
        </v-select>
      </div>
      <div class="col-sm-12 col-md-12 mb-2">
        <div class="row">
          <div class="col-12">
            <label> {{ $t('message.settings.template.checklist.tasks') }} </label>
            <coozzy-form-tag
              v-model="initialTasks"
              :initial="initialTasks"
              :from-checklist="true"
              :allow-duplicates="true"
              @input="checklistChanged"
              @suggestion-removed="suggestionRemoved" />
          </div>
          <div
            v-if="listTasks.length > 0"
            class="col-12">
            <draggable
              v-model="listTasks"
              @end="updateOrder">
              <template
                v-for="task in listTasks">
                <div
                  :key="task.id"
                  class="item-task row">
                  <div class="col-6 align-content-center">
                    <coozzy-form-input
                      v-if="editingTask && editingTask.id === task.id"
                      v-model="task.title"
                      class="input-task"
                      @blur="finishEdit(task)"
                      @keyup.enter="finishEdit(task)" />
                    <p v-else>
                      {{ task.title }}
                    </p>
                  </div>
                  <div class="col-5">
                    <v-select
                      :key="task.id"
                      v-model="task.assignee"
                      :filter="searchAssignee"
                      :options="listAllEmployeesTasks"
                      class="assignee"
                      :components="{ Deselect: CustomDeselect }"
                      @option:selected="(option) => assigneeTaskChanged(option, task)"
                      @option:deselected="assigneeTaskRemoved(task)">
                      <template #option="option">
                        <img
                          :alt="option.label"
                          :src="option.value?.profile?.pictureUrl || '/avatar.png'"
                          class="align-self-center avatar"
                          height="15px">
                        {{ option.label }} <b>{{ option.company ? '-' + option.company : '' }}</b>
                      </template>
                      <template #selected-option="option">
                        <div>
                          <img
                            :alt="option.label"
                            :src="option.value?.profile?.pictureUrl || '/avatar.png'"
                            class="align-self-center avatar"
                            height="15">
                          {{ option.label }} <b>{{ option.company ? '-' + option.company : '' }}</b>
                        </div>
                      </template>
                    </v-select>
                  </div>
                  <div class="col p-0 align-content-center">
                    <coozzy-button
                      v-if="!(editingTask && editingTask.id === task.id)"
                      design="transparent"
                      class="p-0 ml-2 mb-1 float-right"
                      @click="editTask(task)">
                      <coozzy-edit-icon />
                    </coozzy-button>
                    <coozzy-button
                      v-if="editingTask && editingTask.id === task.id"
                      design="transparent"
                      class="p-0 ml-2 mb-1 float-right"
                      @click="finishEdit(task)">
                      <coozzy-save-icon />
                    </coozzy-button>
                    <coozzy-button
                      v-else
                      design="transparent"
                      class="p-0 ml-1 mb-1 float-right"
                      @click="deleteTask(task)">
                      <coozzy-delete-icon />
                    </coozzy-button>
                  </div>
                </div>
              </template>
            </draggable>
          </div>
        </div>
      </div>
      <div class="col-sm-12 col-md-12">
        <label for="file">{{ $t('message.generic.attachments') }}</label>
        <coozzy-form-file-input
          id="file"
          ref="file-input"
          accept=".docx"
          @change="addFile" />
        <div class="row">
          <div
            v-for="media in mediasList"
            :key="media.id"
            class="media-block col">
            <div
              v-if="['image/jpeg', 'image/png', 'image/gif'].indexOf(media.mimeType) !== -1"
              :style="{ 'background-image': 'url(' + media.url + ')' }"
              class="uploaded-file my-2">
              <coozzy-button
                class="delete-icon"
                design="transparent"
                @click="deleteMedia(media.id)">
                <coozzy-delete-icon />
              </coozzy-button>
            </div>
            <div
              v-else
              class="file-block p-1 my-2">
              <a
                :href="media.url"
                data-v-09485260=""
                target="_blank">
                {{ media.filename }}
              </a>
              <coozzy-button
                class="delete-icon"
                design="transparent"
                @click="deleteMedia(media.id)">
                <coozzy-delete-icon />
              </coozzy-button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      class="w-100 mt-2">
      <coozzy-button
        class="float-left mb-0 border"
        design="transparent"
        size="small"
        @click="hide()">
        {{ $t('message.generic.cancel') }}
      </coozzy-button>
      <coozzy-button
        v-if="templateEdit === null"
        :disabled="disableBtns"
        class="float-right mb-0"
        design="green"
        size="small"
        @click="saveChecklistTemplate">
        {{ $t('message.generic.create') }}
      </coozzy-button>
      <coozzy-button
        v-else
        :disabled="disableBtns"
        class="float-right mb-0"
        design="green"
        size="small"
        @click="saveChecklistTemplate">
        {{ $t('message.generic.form.save') }}
      </coozzy-button>
    </div>
  </b-modal>
</template>

<script>
import MediaApi from '../../misc/apis/MediaApi'
import CoozzyFormInput from '../../framework/components/form/input/CoozzyFormInput'
import CoozzyButton from '../../framework/components/button/CoozzyButton'
import { required } from 'vuelidate/lib/validators'
import Vue from 'vue'
import CoozzyFormFileInput from '../../framework/components/form/fileInput/CoozzyFormFileInput'
import { user } from '@/mixins/user'
import { media } from '@/mixins/media'
import employee from '@/store/modules/employee'
import { mapGetters } from 'vuex'
import TicketApi from '@/misc/apis/TicketApi'
import UserApi from '@/misc/apis/UserApi'
import CoozzyFormTag from '@/framework/components/form/input/CoozzyFormTag.vue'
import CoozzyDeleteIcon from '@/framework/components/icons/CoozzyDeleteIcon.vue'
import CoozzySaveIcon from '@/framework/components/icons/CoozzySaveIcon.vue'
import CoozzyEditIcon from '@/framework/components/icons/CoozzyEditIcon.vue'
import draggable from 'vuedraggable'

export default {
  name: 'ChecklistTemplateModal',
  components: {
    draggable,
    CoozzyFormFileInput,
    CoozzyEditIcon,
    CoozzySaveIcon,
    CoozzyDeleteIcon,
    CoozzyFormTag,
    CoozzyButton,
    CoozzyFormInput
  },
  mixins: [user, media, employee],
  props: {
    templateEdit: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      CustomDeselect: {
        functional: true,
        render(h, context) {
          const title = context.parent.$t ? context.parent.$t('message.ticketOverview.ticket.removeAssignee') : 'Clear selection'
          return h(
            'span',
            {
              attrs: { title },
              class: 'vs__deselect',
              on: {
                mousedown: (e) => {
                  e.preventDefault()
                  context.parent.$emit('option:deselected')
                }
              }
            },
            [
              h(
                'svg',
                {
                  attrs: {
                    width: '10',
                    height: '10',
                    xmlns: 'http://www.w3.org/2000/svg'
                  }
                },
                [
                  h('path', {
                    attrs: {
                      d: 'M6.895455 5l2.842897-2.842898c.348864-.348863.348864-.914488 0-1.263636L9.106534.261648c-.348864-.348864-.914489-.348864-1.263636 0L5 3.104545 2.157102.261648c-.348863-.348864-.914488-.348864-1.263636 0L.261648.893466c-.348864.348864-.348864.914489 0 1.263636L3.104545 5 .261648 7.842898c-.348864.348863-.348864.914488 0 1.263636l.631818.631818c.348864.348864.914773.348864 1.263636 0L5 6.895455l2.842898 2.842897c.348863.348864.914772.348864 1.263636 0l.631818-.631818c.348864-.348864.348864-.914489 0-1.263636L6.895455 5z'
                    }
                  })
                ]
              )
            ]
          )
        }
      },
      disableBtns: false,
      template: {
        assigneeUserId: '',
        checklistItemTitles: [],
        checklistItems: [],
        mediaIds: [],
        name: '',
        ownerId: this.accountId,
        usePropertyAssignee: false
      },
      listAllEmployeesChecklist: [],
      listAllEmployeesTasks: [],
      currentEmployeeList: [],
      ownerAccounts: [],
      editingTask: null,
      selectedTask: null,
      listTasks: [],
      mediasList: [],
      initialTasks: [],
      propertyAssignee: {
        label: this.$t('message.settings.template.checklist.propertyAssignee'),
        value: { id: 'propertyAssignee', profile: { firstName: '', lastName: '', pictureUrl: '' } },
        company: ''
      },
      assigneeChecklist: {
        label: this.$t('message.settings.template.checklist.assigneeOfChecklist'),
        value: { id: 'assigneeChecklist', profile: { firstName: '', lastName: '', pictureUrl: '' } },
        company: ''
      }
    }
  },
  computed: {
    employeeList() {
      if (this.getEmployees().length > 0) {
        return this.getEmployees().filter(x => !x.blocked)
      }
      return []
    },
    assignee: {
      get() {
        return this.template.assigneeUserId !== '' && !this.template.usePropertyAssignee ? this.listAllEmployeesChecklist.find(x => x.value.id === this.template.assigneeUserId) : this.template.usePropertyAssignee ? this.propertyAssignee : null
      },
      set(value) {
        if (!value) {
          this.template.assigneeUserId = ''
          this.template.usePropertyAssignee = false
          return
        }
        if (value.value.id === 'propertyAssignee') {
          this.template.usePropertyAssignee = true
          this.template.assigneeUserId = ''
        } else {
          this.template.usePropertyAssignee = false
          this.template.assigneeUserId = value.value.id
        }
        if (this.templateEdit === null) {
          this.changeAssigneeTasks(value.value.id)
        }
      }
    }
  },
  watch: {},
  mounted() {
    this.loadData()
  },
  methods: {
    ...mapGetters('employee', ['getEmployees']),
    async loadData() {
      await this.getListEmployees([this.accountId])
      if (this.templateEdit !== null) {
        this.template = JSON.parse(JSON.stringify(this.templateEdit))
        if (this.template.mediaIds.length !== 0) {
          for (let index = 0; index < this.template.mediaIds.length; index++) {
            MediaApi.getMediaByID(this.template.mediaIds[index])
              .then(response => {
                this.mediasList.push(response.media)
              })
              .catch(e => {
                console.log(e)
                Vue.toasted.show(this.$t('message.loadingErrors.media'), { type: 'error' })
              })
          }
        } else {
          this.template.mediaIds = []
          this.mediasList = []
        }
        if (this.template.checklistItemTitles.length > 0 || this.template.checklistItems.length > 0) {
          if (this.template.checklistItems.length === 0) {
            this.listTasks = this.template.checklistItemTitles.map((item, index) => {
              return {
                id: 'task_' + index,
                order: index + 1,
                title: item,
                assigneeUserId: this.template.assigneeUserId,
                assignee: this.template.assigneeUserId !== '' && !this.template.usePropertyAssignee ? this.listAllEmployeesChecklist.find(x => x.value.id === this.template.assigneeUserId) : this.assigneeChecklist,
                useChecklistAssignee: false
              }
            })
          } else {
            this.listTasks = this.template.checklistItems.map((item, index) => {
              return {
                id: 'task_' + index,
                assignee: item.assigneeUserId !== '' && !item.useChecklistAssignee ? this.listAllEmployeesTasks.find(x => x.value.id === item.assigneeUserId) : item.useChecklistAssignee ? this.assigneeChecklist : '',
                ...item
              }
            })
          }
          this.initialTasks = this.listTasks.map(task => task.title)
        } else {
          this.listTasks = []
          this.initialTasks = []
        }
      } else {
        this.template = {
          assigneeUserId: '',
          checklistItemTitles: [],
          checklistItems: [],
          mediaIds: [],
          name: '',
          ownerId: this.accountId,
          usePropertyAssignee: false
        }
        this.mediasList = []
      }
    },
    changeAssigneeTasks(id) {
      this.listTasks.forEach(task => {
        if (id === 'propertyAssignee') {
          task.assigneeUserId = ''
          task.assignee = this.assigneeChecklist
          task.useChecklistAssignee = true
        } else {
          task.assigneeUserId = id
          task.assignee = this.listAllEmployeesTasks.find(x => x.value.id === id)
          task.useChecklistAssignee = false
        }
      })
      this.template.checklistItems = this.listTasks
    },
    assigneeTaskChanged(option, task) {
      if (option) {
        if (option.value.id === 'assigneeChecklist') {
          task.useChecklistAssignee = true
          task.assigneeUserId = ''
        } else {
          task.useChecklistAssignee = false
          task.assigneeUserId = option.value.id
        }
      } else {
        task.useChecklistAssignee = false
        task.assigneeUserId = ''
      }
      this.template.checklistItems = this.listTasks
    },
    assigneeTaskRemoved(task) {
      task.useChecklistAssignee = false
      task.assigneeUserId = ''
      this.template.checklistItems = this.listTasks
    },
    updateOrder() {
      // Update the order attribute based on the new arrangement
      this.listTasks.forEach((task, index) => {
        task.order = index + 1
      })
      // Update the template checklistItemTitles to reflect the new order
      this.template.checklistItems = this.listTasks
      this.initialTasks = this.listTasks.map(task => task.title)
    },
    addAvailableEmployees() {
      const array = []
      if (this.employeeList.length > 0) {
        this.employeeList.filter(x => (x.blocked === false && x.deleted === false)).forEach((employee) => {
          const accountName = this.ownerAccounts.find(x => x.id === employee.accountId)
          array.push({
            label: (employee.profile.firstName ? employee.profile.firstName + ' ' : '') + employee.profile.lastName,
            value: employee,
            company: (accountName ? ' ' + (accountName.company ? accountName.company?.name : accountName.individual.firstName + ' ' + accountName.individual.lastName) : '')
          })
        })
      }
      if (this.currentEmployeeList.length !== 0) {
        this.currentEmployeeList.filter(x => (x.blocked === false && x.deleted === false)).forEach((employee) => {
          const accountName = this.ownerAccounts.find(x => x.id === employee.accountId)
          array.push({
            label: ((employee.profile.firstName ? employee.profile.firstName + ' ' : '') + employee.profile.lastName),
            value: employee,
            company: (accountName ? ' ' + (accountName.company ? accountName.company?.name : accountName.individual.firstName + ' ' + accountName.individual.lastName) : '')
          })
        })
      }

      const seen = new Set()
      const filtered = array.filter(({ value }) => {
        const isSupportUser = value.email.startsWith('support.') && value.email.endsWith('@my-prop.ch')
        if (seen.has(value.id) || isSupportUser) return false
        seen.add(value.id)
        return true
      })

      const accountUsers = filtered.filter(x => parseInt(x.value.accountId) === this.accountId).sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1))
      const otherUsers = filtered.filter(x => parseInt(x.value.accountId) !== this.accountId).sort((a, b) => (a.company > b.company ? 1 : -1))
      this.listAllEmployeesChecklist = accountUsers.concat(otherUsers)
      this.listAllEmployeesChecklist.unshift(this.propertyAssignee)

      this.listAllEmployeesTasks = accountUsers.concat(otherUsers)
      this.listAllEmployeesTasks.unshift(this.assigneeChecklist)
      if (this.template.assigneeUserId === '' && this.template.usePropertyAssignee === false) {
        this.assignee = this.listAllEmployeesChecklist.find(y => y.value.id === this.userId)
      }
    },
    async getListEmployees(listIds) {
      if (listIds.length > 0) {
        await TicketApi.listTicketingEnabled(listIds).then(async (rsp) => {
          if (rsp.enabledAccountIds.length > 0) {
            await UserApi.listAllEmployees(rsp.enabledAccountIds).then(async (response1) => {
              this.currentEmployeeList = response1.employees
              if (this.currentEmployeeList.length > 0) {
                const ids = [...new Set(this.currentEmployeeList.map(x => x.accountId).concat(this.employeeList.map(x => x.accountId)))]
                if (ids.length > 0) {
                  await UserApi.listAccountsByIds(ids)
                    .then(accounts => {
                      this.ownerAccounts = accounts.accounts
                      this.addAvailableEmployees()
                    })
                }
              } else {
                this.addAvailableEmployees()
              }
            })
          }
        })
      }
    },
    searchAssignee(options, search) {
      return search.length
        ? options.filter(item => item.label.toLowerCase().includes(search.toLowerCase()) || item.company.toLowerCase().includes(search.toLowerCase()))
        : options
    },
    checklistChanged(list) {
      if (list.length > 0) {
        const item = list[list.length - 1]

        if (!item || typeof item !== 'string' || item.trim() === '') {
          return
        }

        const internalId = Date.now() + Math.floor(Math.random() * 1000)
        const id = 'task_' + internalId
        const task = {
          id: id,
          title: item.trim(),
          order: 1,
          assigneeUserId: this.template.assigneeUserId,
          assignee: this.template.assigneeUserId !== '' && !this.template.usePropertyAssignee ? this.listAllEmployeesChecklist.find(x => x.value.id === this.template.assigneeUserId) : this.assigneeChecklist,
          useChecklistAssignee: this.template.usePropertyAssignee
        }
        this.listTasks.push(task)
        this.updateOrder()
        this.template.checklistItems = this.listTasks
      }
    },
    suggestionRemoved(item) {
      this.listTasks = this.listTasks.filter(x => x.name !== item)
      this.updateOrder()
      this.template.checklistItems = this.listTasks
    },
    editTask(task) {
      this.editingTask = { ...task }
    },
    finishEdit(task) {
      if (task) {
        let taskIndex = this.listTasks.findIndex(
          (r) => r.id === task.id
        )
        if (taskIndex !== -1) {
          this.listTasks.splice(taskIndex, 1, task)
          this.updateOrder()
          this.template.checklistItems = this.listTasks
        }
      }
      this.editingTask = null
    },
    deleteTask(task) {
      this.listTasks = this.listTasks.filter(r => task.id !== r.id)
      this.updateOrder()
      this.template.checklistItems = this.listTasks
    },
    saveChecklistTemplate() {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        this.disableBtns = true
        if (this.templateEdit !== null) {
          TicketApi.updateCheckListTemplate(
            this.template.id,
            this.template
          ).then(() => {
            this.hide()
            this.disableBtns = false
            Vue.toasted.show(this.$t('message.settings.template.checklistSavedSuccessfully'), { type: 'success' })
            this.$emit('change-success')
            this.$v.$reset()
          }).catch(e => {
            this.disableBtns = false
            console.log(e)
            Vue.toasted.show(this.$t('message.savingErrors.template'), { type: 'error' })
          })
        } else {
          TicketApi.createCheckListTemplate(this.template).then(() => {
            this.$v.$reset()
            this.hide()
            this.disableBtns = false
            Vue.toasted.show(this.$t('message.settings.template.checklistSavedSuccessfully'), { type: 'success' })
            this.$emit('change-success')
          }).catch(e => {
            this.disableBtns = false
            console.log(e)
            Vue.toasted.show(this.$t('message.savingErrors.template'), { type: 'error' })
          })
        }
      }
    },
    async addFile(event) {
      const files = await this.getFilesFromEvent(event)
      if (files && files.length > 0) {
        this.mediaUpload = files[0]
        await this.sendFile()
      }
      this.$nextTick(() => {
        if (this.$refs['file-input']) {
          this.$refs['file-input'].resetPlaceholder()
        }
      })
    },
    async sendFile() {
      const selectedFile = this.mediaUpload ? [this.mediaUpload] : []
      this.disableBtns = true
      const response = await this.uploadMedia(selectedFile)
      if (response?.length > 0) {
        const media = response[0]
        this.mediasList.push(media)
        this.template.mediaIds.push(media.id)
        this.mediaUpload = null
      }
      this.disableBtns = false
    },
    deleteMedia(id) {
      if (this.template.mediaIds) {
        this.template.mediaIds = this.template.mediaIds.filter(e => e !== id)
      }
      if (this.mediasList) {
        this.mediasList = this.mediasList.filter(el => el.id !== id)
      }
    },
    show() {
      this.$refs['checklist-template-modal'].show()
    },
    hide() {
      this.$refs['checklist-template-modal']?.hide()
    }
  },
  validations() {
    return {
      template: {
        name: {
          required
        }
      },
      assignee: {
        required
      }
    }
  }
}
</script>

<style lang="scss" scoped>
label {
  margin-bottom: 0;
}

:deep(.assignee .vs__clear) {
  box-shadow: none;
}

.item-task {
  padding: 10px;
  margin-bottom: 0.25rem;
  margin-top: 0.25rem;
  border: 1px solid #ddd;
  border-radius: 3px;
  background-color: #f5f5f5;

  p, .input-task {
    margin: 0;
    width: 95%;
  }
}

.item-task:first-child {
  margin-top: 1rem;
}

:deep .is-invalid .vs__dropdown-toggle {
  border-color: #dc3545;
  padding-right: calc(1.5em + 0.75rem) !important;
  background-image: url("../../assets/icon/is-invalid.svg");
  background-repeat: no-repeat;
  background-position: right calc(0.375em + 0.1875rem) center;
  background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
}
//
//.media-block {
//  max-width: 33%;
//  margin-right: 0.5%;
//
//  .uploaded-file {
//    background-position: center center;
//    background-size: cover;
//    min-height: 7em;
//  }
//
//  .file-block {
//    min-height: 7em;
//    background: $color-bg-grey-dark;
//
//    a {
//      color: $color-text-grey;
//      overflow-wrap: break-word;
//    }
//  }
//}
//
//.custom-list {
//  margin-left: 1.5em;
//  .custom-list-item {
//    padding: .3em 1em;
//    &:before {
//      content: "";
//      display: inline-block;
//      width: .5em;
//      height: .5em;
//      margin-right: .5em;
//      border-radius: 50%;
//      background-color: $color-text-grey;
//    }
//  }
//}
//
//:deep(.modal-dialog) {
//  max-width: 1200px !important;
//}

</style>
